import { isNil, isNotNil } from "@etudescarre/utils";
import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import { apiClient } from "@/lib/api/client";
import { DocumentTable } from "../components/document-table/document-table";
import {
  FilterBar,
  type FilterOption,
} from "../components/document-table/filter-bar";
import type { LotData } from "../components/document-table/types";
import { useDocumentActions } from "../components/document-table/use-document-actions";

type FetchState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; data: LotData[] };

// Filtres appliqués par l'API : dès que l'un d'eux est actif, la liste
// affichée ne peut plus servir de source aux options de filtre.
const SERVER_FILTER_PARAMS = [
  "lot",
  "presta",
  "nature",
  "zone",
  "indexMode",
  "author",
  "number",
  "name",
  "zoneName",
  "hideObsolete",
  "needsUpdate",
] as const;

export function DocumentsList() {
  const { yardId } = useParams<{ yardId: string }>();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<FetchState>({ status: "loading" });
  const [filterSourceLots, setFilterSourceLots] = useState<LotData[] | null>(
    null,
  );
  // Chantier dont la source des options de filtre est chargée : tous ses
  // documents, sans filtre, pour que les cases à cocher ne restreignent pas
  // le bloc Provenance.
  const filterSourceYardRef = useRef<string | null>(null);

  // Counter to force re-fetch after mutations (BPE, diffusion)
  const [refreshKey, setRefreshKey] = useState(0);

  // Sélection du filtre "Documents diffusés à" (ids d'équipes),
  // appliquée côté client (picto "visa en retard" + estompage des versions).
  const selectedDiffuseATeamIds = (searchParams.get("diffuseA") ?? "")
    .split(",")
    .map(Number)
    .filter((n) => Number.isFinite(n) && n > 0);

  // Serialize search params to a stable string for the effect dependency.
  // This avoids re-fetching when the searchParams object reference changes
  // but the actual values haven't.
  const searchParamsString = searchParams.toString();
  const hasServerFilters = SERVER_FILTER_PARAMS.some(
    (key) => (searchParams.get(key) ?? "") !== "",
  );

  useEffect(() => {
    if (
      isNil(yardId) ||
      !hasServerFilters ||
      filterSourceYardRef.current === yardId
    ) {
      return;
    }

    let cancelled = false;

    async function fetchFilterSource() {
      const { data, error } = await apiClient.GET(
        "/api/yards/{yardId}/documents",
        { params: { path: { yardId: yardId ?? "" } } },
      );

      if (cancelled) return;

      if (isNotNil(error) || isNil(data)) {
        filterSourceYardRef.current = null;
        setFilterSourceLots(null);
        return;
      }

      filterSourceYardRef.current = yardId ?? null;
      setFilterSourceLots(data.data.lots);
    }

    fetchFilterSource();
    return () => {
      cancelled = true;
    };
  }, [yardId, hasServerFilters]);

  // Fetch documents -- depends on yardId, URL search params, and refreshKey
  useEffect(() => {
    // refreshKey is used to trigger re-fetch after mutations
    void refreshKey;
    if (isNil(yardId)) return;

    let cancelled = false;

    async function fetchDocuments() {
      setState((prev) =>
        prev.status === "success" ? prev : { status: "loading" },
      );

      const params = new URLSearchParams(searchParamsString);
      const queryParams: Record<string, string> = {};
      const lot = params.get("lot");
      const presta = params.get("presta");
      const nature = params.get("nature");
      const zone = params.get("zone");
      const indexMode = params.get("indexMode");
      const author = params.get("author");
      const number = params.get("number");
      const name = params.get("name");
      const zoneName = params.get("zoneName");
      const hideObsolete = params.get("hideObsolete");
      const needsUpdate = params.get("needsUpdate");
      if (lot) queryParams.lot = lot;
      if (presta) queryParams.presta = presta;
      if (nature) queryParams.nature = nature;
      if (zone) queryParams.zone = zone;
      if (indexMode) queryParams.indexMode = indexMode;
      if (author) queryParams.author = author;
      if (number) queryParams.number = number;
      if (name) queryParams.name = name;
      if (zoneName) queryParams.zoneName = zoneName;
      if (hideObsolete) queryParams.hideObsolete = hideObsolete;
      if (needsUpdate) queryParams.needsUpdate = needsUpdate;

      const { data, error } = await apiClient.GET(
        "/api/yards/{yardId}/documents",
        {
          params: {
            path: { yardId: yardId ?? "" },
            query: queryParams,
          },
        },
      );

      if (cancelled) return;

      if (error || isNil(data)) {
        setState({ status: "error", error: "Erreur lors du chargement" });
        return;
      }

      setState({ status: "success", data: data.data.lots as LotData[] });
      if (!hasServerFilters) {
        filterSourceYardRef.current = yardId ?? null;
        setFilterSourceLots(data.data.lots);
      }
    }

    fetchDocuments();
    return () => {
      cancelled = true;
    };
  }, [yardId, searchParamsString, refreshKey, hasServerFilters]);

  const { tableProps, overlays, diffusionTeams } = useDocumentActions({
    yardId,
    lots: state.status === "success" ? state.data : [],
    selectedDiffuseATeamIds,
    onChanged: () => setRefreshKey((k) => k + 1),
  });

  // Options du filtre "Documents diffusés à" : équipes du chantier
  // effectivement destinataires d'au moins une diffusion validée.
  const diffusedTeamIds = new Set<number>();
  if (state.status === "success") {
    for (const lot of state.data) {
      for (const presta of lot.prestas) {
        for (const doc of presta.documents) {
          for (const v of doc.versions) {
            for (const teamId of v.diffusedTeamIds ?? []) {
              diffusedTeamIds.add(teamId);
            }
          }
        }
      }
    }
  }
  const diffuseATeamOptions: FilterOption[] = diffusionTeams
    .filter((t) => diffusedTeamIds.has(t.id))
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((t) => ({ label: t.name, value: String(t.id) }));

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <FilterBar
        lots={
          filterSourceLots ?? (state.status === "success" ? state.data : [])
        }
        diffuseATeamOptions={diffuseATeamOptions}
        canMarkBpe={tableProps.canMarkBpe}
      />

      {state.status === "loading" && (
        <div className="flex items-center justify-center py-16">
          <p className="text-zinc-500">Chargement des documents...</p>
        </div>
      )}

      {state.status === "error" && (
        <div className="flex flex-col items-center justify-center py-16 gap-2">
          <p className="text-red-600">{state.error}</p>
        </div>
      )}

      {state.status === "success" && (
        <div className="flex min-h-0 flex-1 flex-col px-4 py-4">
          <DocumentTable lots={state.data} {...tableProps} />
        </div>
      )}

      {overlays}
    </div>
  );
}
